import { z } from 'zod';

export const DiagnoseCitationSchema = z.object({
  source: z.string(),
  title: z.string(),
  url: z.string().url().optional(),
  snippet: z.string().optional(),
});

export const DiagnoseResponseSchema = z.object({
  diagnosis: z.string(),
  likelihood: z.enum(['low', 'medium', 'high']),
  citations: z.array(DiagnoseCitationSchema),
  followUpQuestions: z.array(z.string()).optional(),
});

export type DiagnoseResponseValidated = z.infer<typeof DiagnoseResponseSchema>;

// Spec 007 — vehicle transfer flow.

const TransferCodeSchema = z.string().regex(/^[A-Z2-9]{8}$/);

const TransferVehicleSummarySchema = z.object({
  year: z.number().int(),
  make: z.string().min(1),
  model: z.string().min(1),
  chassisCode: z.string().min(1),
});

export const TransferInitiateResponseSchema = z.object({
  transferId: z.string().min(1),
  code: TransferCodeSchema,
  expiresAt: z.string().min(1),
});

export const TransferValidateResponseSchema = z.object({
  vehicleSummary: TransferVehicleSummarySchema,
  code: TransferCodeSchema,
});

export const TransferDecisionResponseSchema = z.object({
  vehicleId: z.string().min(1),
  decision: z.enum(['accept', 'decline']),
  vehicleSummary: TransferVehicleSummarySchema.optional(),
});

export const TransferCancelResponseSchema = z.object({
  transferId: z.string().min(1),
  status: z.literal('cancelled'),
});

// E4 — service entry wire schema (used to validate POST /v1/vehicles/:id/service 201 response).

export const ServiceEntrySchema = z.object({
  id: z.string().min(1),
  vehicleId: z.string().min(1),
  summary: z.string().min(1),
  performedAt: z.string().optional(),
  // mileage + notes are Option<T> WITHOUT skip_serializing_if in the Rust
  // ServiceEntry, so the server emits them as explicit `null` (not absent) —
  // .nullish() accepts both null and undefined. The rest are skip-when-None.
  mileage: z.number().nullish(),
  performedBy: z.string().optional(),
  laborHours: z.number().optional(),
  notes: z.string().nullish(),
  systems: z.array(z.string()).optional(),
  // The server-authored entry carries `authorUid` (the owner); it has no
  // `createdBy` field (that was a client-only stamp on the old write path).
  authorUid: z.string().optional(),
  createdBy: z.string().optional(),
  createdAt: z.string(),
  source: z.enum(['manual', 'shop_invoice', 'self_performed']).optional(),
  vendor: z.string().optional(),
  invoiceImageRef: z.string().optional(),
  lineItems: z
    .array(
      z.object({
        description: z.string(),
        kind: z.enum(['part', 'labor', 'fee', 'tax', 'shipping', 'discount', 'other']).optional(),
        partNumber: z.string().optional(),
        quantity: z.number().optional(),
        amountMinor: z.number(),
        currency: z.string().optional(),
      }),
    )
    .optional(),
  totalMinor: z.number().optional(),
  currency: z.string().optional(),
  supersedesEntryId: z.string().optional(),
  // Spec 011 (Slice 1) — the client sends beforePhotoId/afterPhotoId (plain
  // photo id strings, per lamport's revised DTO); the server resolves each
  // to the authoritative {photoId, sha256} and seals that — the client never
  // handles or sees the sha256. Declared here as optional strings for
  // reading a sealed entry back; this is our best assumption for the
  // response shape pending confirmation once #248b's exact response DTO is
  // visible (zod ignores undeclared/mismatched response keys by default, so
  // this can't break parsing either way).
  beforePhotoId: z.string().optional(),
  afterPhotoId: z.string().optional(),
  // Spec 011 — independent of `source`/`EntrySource`; default false.
  // E3 display rule (lamport): a caller rendering "self-performed" /
  // value-invested MUST treat `source === 'self_performed' || selfPerformed
  // === true` as self-performed — these are two different axes (the legacy
  // enum value predates this field and both can independently be true).
  selfPerformed: z.boolean().optional(),
  // laborHoursSource is server-set to "manual" in Slice 1 — the client never
  // sends it. Declared here only for forward parse-compatibility once
  // Slice 2's EXIF suggestion starts setting "photo_estimated"/"edited".
  laborHoursSource: z.enum(['manual', 'photo_estimated', 'edited']).optional(),
});

export type ServiceEntryValidated = z.infer<typeof ServiceEntrySchema>;

// E5 — Modification + concours-impact schema (client-direct Firestore).
// Mirrors the Modification interface in domain.ts (camelCase, ISO timestamps,
// integer minor-units for money). concoursImpact defaults to 'none' so that
// any pre-existing doc without the field deserialises safely.

export const ModificationSchema = z.object({
  id: z.string().min(1),
  vehicleId: z.string().min(1),
  category: z.enum([
    'engine',
    'suspension',
    'brakes',
    'interior',
    'exterior',
    'electrical',
    'other',
  ]),
  title: z.string().min(1),
  description: z.string().optional(),
  installedAt: z.string().optional(),
  concoursImpact: z.enum(['none', 'minor', 'moderate', 'major']).default('none'),
  reversible: z.boolean().default(false),
  oeRetained: z.boolean().default(false),
  judgeVisibility: z.enum(['hidden', 'subtle', 'obvious']).default('hidden'),
  restorationNote: z.string().optional(),
  // Integer minor units (e.g. cents) — no floats.
  restorationCostMinor: z.number().int().nonnegative().optional(),
  currency: z.string().optional(),
  createdBy: z.string().min(1),
  createdAt: z.string().min(1),
  updatedAt: z.string().optional(),
});

export type ModificationValidated = z.infer<typeof ModificationSchema>;

// E4 — task completion response schema.
// POST /v1/vehicles/:id/tasks/:tid/complete returns a sealed ServiceEntry.
// The server sums timeLog → durationMinutes → labor_hours; the client never
// computes labour. Re-uses ServiceEntrySchema since the response IS a
// ServiceEntry (source: 'self_performed').
export const TaskCompleteResponseSchema = ServiceEntrySchema;
export type TaskCompleteResponseValidated = ServiceEntryValidated;
